import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { AuthManagerService } from '../services/auth-manager/auth-manager.service';
import { UserService } from '../services/user/user.service';

const TOKEN_URL = '/api/v2/identity/token';
const REFRESH_URL = '/api/v2/identity/refresh';

@Injectable()
export class UnauthInterceptor implements HttpInterceptor {

  constructor(
    private router: Router,
    private authManagerService: AuthManagerService,
    private userService: UserService,
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const skipInterceptor = request.headers.getAll('Skip-Interceptors')?.includes('unauth');
    if (skipInterceptor) {
      return next.handle(request);
    }
    return next.handle(request)
      .pipe(
        catchError((err: any) => {
          if (err instanceof HttpErrorResponse && request.url.indexOf(`${environment.apiUrl}/api/`) === 0) {
            if (err.status === 401) {
              // A failed refresh is handled by the request that triggered it
              if (request.url.includes(REFRESH_URL)) {
                return throwError(() => err);
              }
              // Wrong credentials on login can't be recovered by refreshing.
              // Without a refresh session (never logged in, or logged out) a leftover cookie must not restore it
              if (request.url.includes(TOKEN_URL) || !this.authManagerService.hasRefreshSession) {
                this.handleUnauthorized();
                return throwError(() => err);
              }
              // Try to get a new JWT with the refresh cookie, then retry the original request once
              return this.authManagerService.refreshToken().pipe(
                catchError(() => {
                  this.handleUnauthorized();
                  return throwError(() => err);
                }),
                switchMap((token) => next.handle(request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))),
              );
            }
          }
          return throwError(() => err);
        }));
  }

  private handleUnauthorized(): void {
    this.authManagerService.removeToken();
    // Skip login redirection on 401 on certain conditions
    if (this.userService.options.disableUnauthRedirect) {
      this.userService.options.disableUnauthRedirect = false;
    } else {
      if (this.userService.options.lastUrlRequested) {
        // In case the user is on a login page, then we don't need to add the redirect queryParam
        if (!this.userService.options.lastUrlRequested.includes('/login') && !this.userService.options.lastUrlRequested.includes('/tok-login')) {
          if (this.userService.options.lastUrlRequested.includes('/claim-pet')){
            this.router.navigate(['/login'], { queryParams: { r: this.userService.options.lastUrlRequested } });
            return
          }
          if(this.userService.options.lastUrlRequested !== '/login-register') {
            this.router.navigate(['/login-register'], { queryParams: { r: this.userService.options.lastUrlRequested } });
          }
        }
      } else {
        this.router.navigate(['/login-register']);
      }
    }
  }
}
